/**
 * Types Page - Complete type effectiveness chart
 */
import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Info, ArrowRight, Shield, Swords, Ban } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { TYPE_CHART } from '../hooks/usePokemon';
import { cn } from '../lib/utils';

// All 18 Pokemon types in order
const ALL_TYPES = [
  'normal', 'fire', 'water', 'electric', 'grass', 'ice',
  'fighting', 'poison', 'ground', 'flying', 'psychic', 'bug',
  'rock', 'ghost', 'dragon', 'dark', 'steel', 'fairy'
];

// Type colors for styling
const TYPE_COLORS = {
  normal: { bg: 'bg-gray-400', text: 'text-gray-900', hex: '#A8A878' },
  fire: { bg: 'bg-orange-500', text: 'text-white', hex: '#F08030' },
  water: { bg: 'bg-blue-500', text: 'text-white', hex: '#6890F0' },
  electric: { bg: 'bg-yellow-400', text: 'text-gray-900', hex: '#F8D030' },
  grass: { bg: 'bg-green-500', text: 'text-white', hex: '#78C850' },
  ice: { bg: 'bg-cyan-300', text: 'text-gray-900', hex: '#98D8D8' },
  fighting: { bg: 'bg-red-700', text: 'text-white', hex: '#C03028' },
  poison: { bg: 'bg-purple-500', text: 'text-white', hex: '#A040A0' },
  ground: { bg: 'bg-amber-600', text: 'text-white', hex: '#E0C068' },
  flying: { bg: 'bg-indigo-300', text: 'text-gray-900', hex: '#A890F0' },
  psychic: { bg: 'bg-pink-500', text: 'text-white', hex: '#F85888' },
  bug: { bg: 'bg-lime-500', text: 'text-white', hex: '#A8B820' },
  rock: { bg: 'bg-stone-500', text: 'text-white', hex: '#B8A038' },
  ghost: { bg: 'bg-violet-700', text: 'text-white', hex: '#705898' },
  dragon: { bg: 'bg-indigo-700', text: 'text-white', hex: '#7038F8' },
  dark: { bg: 'bg-stone-700', text: 'text-white', hex: '#705848' },
  steel: { bg: 'bg-slate-400', text: 'text-gray-900', hex: '#B8B8D0' },
  fairy: { bg: 'bg-pink-300', text: 'text-gray-900', hex: '#EE99AC' }
};

// Spanish type translations
const TYPE_NAMES = {
  normal: { en: 'Normal', es: 'Normal' },
  fire: { en: 'Fire', es: 'Fuego' },
  water: { en: 'Water', es: 'Agua' },
  electric: { en: 'Electric', es: 'Eléctrico' },
  grass: { en: 'Grass', es: 'Planta' },
  ice: { en: 'Ice', es: 'Hielo' },
  fighting: { en: 'Fighting', es: 'Lucha' },
  poison: { en: 'Poison', es: 'Veneno' },
  ground: { en: 'Ground', es: 'Tierra' },
  flying: { en: 'Flying', es: 'Volador' },
  psychic: { en: 'Psychic', es: 'Psíquico' },
  bug: { en: 'Bug', es: 'Bicho' },
  rock: { en: 'Rock', es: 'Roca' },
  ghost: { en: 'Ghost', es: 'Fantasma' },
  dragon: { en: 'Dragon', es: 'Dragón' },
  dark: { en: 'Dark', es: 'Siniestro' },
  steel: { en: 'Steel', es: 'Acero' },
  fairy: { en: 'Fairy', es: 'Hada' }
};

// TypeBadge component
function TypeBadge({ type, size = 'md', showName = true }) {
  const { language } = useApp();
  const colors = TYPE_COLORS[type];
  const name = TYPE_NAMES[type]?.[language] || type;
  
  const sizeClasses = {
    xs: 'text-[10px] px-1 py-0.5',
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5'
  };
  
  return (
    <span 
      className={cn(
        'rounded font-semibold uppercase tracking-wide inline-flex items-center justify-center',
        colors?.bg,
        colors?.text,
        sizeClasses[size]
      )}
      data-testid={`type-badge-${type}`}
    >
      {showName ? name : type.charAt(0).toUpperCase()}
    </span>
  );
}

// Calculate effectiveness from attacking type to defending type
function getEffectiveness(attackingType, defendingType) {
  const defender = TYPE_CHART[defendingType];
  if (!defender) return 1;
  
  if (defender.immuneTo?.includes(attackingType)) return 0;
  if (defender.weakTo?.includes(attackingType)) return 2;
  if (defender.resistantTo?.includes(attackingType)) return 0.5;
  return 1;
}

// EffectivenessCell component
function EffectivenessCell({ value }) {
  const getBgColor = () => {
    switch (value) {
      case 0: return 'bg-gray-800 text-gray-400';
      case 0.5: return 'bg-red-900/50 text-red-300';
      case 2: return 'bg-green-900/50 text-green-300';
      default: return 'bg-transparent';
    }
  };
  
  const getLabel = () => {
    switch (value) {
      case 0: return '0';
      case 0.5: return '½';
      case 2: return '2';
      default: return '';
    }
  };
  
  return (
    <td className={cn('w-8 h-8 text-center text-xs font-bold border border-border/30', getBgColor())}>
      {getLabel()}
    </td>
  );
}

// Calculate what types this attacking type is super effective against
function getSuperEffectiveAgainst(attackingType) {
  const superEffective = [];
  ALL_TYPES.forEach(defendingType => {
    const defender = TYPE_CHART[defendingType];
    if (defender?.weakTo?.includes(attackingType)) {
      superEffective.push(defendingType);
    }
  });
  return superEffective;
}

// Calculate what types this attacking type is not very effective against
function getNotVeryEffectiveAgainst(attackingType) {
  const notVeryEffective = [];
  ALL_TYPES.forEach(defendingType => {
    const defender = TYPE_CHART[defendingType];
    if (defender?.resistantTo?.includes(attackingType)) {
      notVeryEffective.push(defendingType);
    }
  });
  return notVeryEffective;
}

// Calculate what types are immune to this attacking type
function getNoEffectAgainst(attackingType) {
  const noEffect = [];
  ALL_TYPES.forEach(defendingType => {
    const defender = TYPE_CHART[defendingType];
    if (defender?.immuneTo?.includes(attackingType)) {
      noEffect.push(defendingType);
    }
  });
  return noEffect;
}

// TypeDetailCard component
function TypeDetailCard({ type }) {
  const { language } = useApp();
  const typeData = TYPE_CHART[type];
  
  // Offensive effectiveness (what this type is super effective against)
  const superEffectiveAgainst = getSuperEffectiveAgainst(type);
  const notVeryEffectiveAgainst = getNotVeryEffectiveAgainst(type);
  const noEffectAgainst = getNoEffectAgainst(type);
  
  const labels = {
    offensive: language === 'es' ? 'Atacando' : 'Attacking',
    defensive: language === 'es' ? 'Defendiendo' : 'Defending',
    superEffective: language === 'es' ? 'Super eficaz contra' : 'Super effective against',
    notVeryEffective: language === 'es' ? 'Poco eficaz contra' : 'Not very effective against',
    noEffectAgainst: language === 'es' ? 'Sin efecto contra' : 'No effect against',
    weakTo: language === 'es' ? 'Débil contra' : 'Weak to',
    resistantTo: language === 'es' ? 'Resistente a' : 'Resistant to',
    immuneTo: language === 'es' ? 'Inmune a' : 'Immune to'
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-xl border border-border p-4"
      data-testid={`type-detail-${type}`}
    >
      {/* Just the type badge without duplicate text */}
      <div className="flex items-center gap-3 mb-4">
        <TypeBadge type={type} size="lg" />
      </div>
      
      {/* Offensive Section */}
      <div className="mb-4 pb-4 border-b border-border">
        <h4 className="text-sm font-semibold text-violet-600 dark:text-violet-400 mb-3 flex items-center gap-1">
          <Swords className="w-4 h-4" />
          {labels.offensive}
        </h4>
        <div className="space-y-3">
          {/* Super effective against */}
          {superEffectiveAgainst.length > 0 && (
            <div>
              <div className="flex items-center gap-1 text-sm text-green-500 mb-1">
                <span>{labels.superEffective} (x2)</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {superEffectiveAgainst.map(t => (
                  <TypeBadge key={t} type={t} size="sm" />
                ))}
              </div>
            </div>
          )}
          
          {/* Not very effective against */}
          {notVeryEffectiveAgainst.length > 0 && (
            <div>
              <div className="flex items-center gap-1 text-sm text-orange-500 mb-1">
                <span>{labels.notVeryEffective} (x0.5)</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {notVeryEffectiveAgainst.map(t => (
                  <TypeBadge key={t} type={t} size="sm" />
                ))}
              </div>
            </div>
          )}
          
          {/* No effect against */}
          {noEffectAgainst.length > 0 && (
            <div>
              <div className="flex items-center gap-1 text-sm text-gray-500 mb-1">
                <span>{labels.noEffectAgainst} (x0)</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {noEffectAgainst.map(t => (
                  <TypeBadge key={t} type={t} size="sm" />
                ))}
              </div>
            </div>
          )}
          
          {superEffectiveAgainst.length === 0 && notVeryEffectiveAgainst.length === 0 && noEffectAgainst.length === 0 && (
            <p className="text-sm text-muted-foreground italic">
              {language === 'es' ? 'Daño neutral contra todos los tipos' : 'Neutral damage against all types'}
            </p>
          )}
        </div>
      </div>
      
      {/* Defensive Section */}
      <div>
        <h4 className="text-sm font-semibold text-blue-600 dark:text-blue-400 mb-3 flex items-center gap-1">
          <Shield className="w-4 h-4" />
          {labels.defensive}
        </h4>
        <div className="space-y-3">
          {/* Weak to */}
          {typeData?.weakTo?.length > 0 && (
            <div>
              <div className="flex items-center gap-1 text-sm text-red-500 mb-1">
                <span>{labels.weakTo} (x2)</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {typeData.weakTo.map(t => (
                  <TypeBadge key={t} type={t} size="sm" />
                ))}
              </div>
            </div>
          )}
          
          {/* Resistant to */}
          {typeData?.resistantTo?.length > 0 && (
            <div>
              <div className="flex items-center gap-1 text-sm text-blue-500 mb-1">
                <span>{labels.resistantTo} (x0.5)</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {typeData.resistantTo.map(t => (
                  <TypeBadge key={t} type={t} size="sm" />
                ))}
              </div>
            </div>
          )}
          
          {/* Immune to */}
          {typeData?.immuneTo?.length > 0 && (
            <div>
              <div className="flex items-center gap-1 text-sm text-gray-500 mb-1">
                <Ban className="w-3 h-3" />
                <span>{labels.immuneTo} (x0)</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {typeData.immuneTo.map(t => (
                  <TypeBadge key={t} type={t} size="sm" />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function Types() {
  const { language } = useApp();
  const [selectedType, setSelectedType] = useState(null);
  const [viewMode, setViewMode] = useState('chart'); // 'chart' or 'cards'
  
  // Generate the full effectiveness matrix
  const effectivenessMatrix = useMemo(() => {
    const matrix = {};
    ALL_TYPES.forEach(attacker => {
      matrix[attacker] = {};
      ALL_TYPES.forEach(defender => {
        matrix[attacker][defender] = getEffectiveness(attacker, defender);
      });
    });
    return matrix;
  }, []);
  
  const texts = {
    title: language === 'es' ? 'Tabla de Tipos' : 'Type Chart',
    subtitle: language === 'es' 
      ? 'Efectividad de tipos en combate' 
      : 'Type effectiveness in battle',
    chartView: language === 'es' ? 'Tabla' : 'Chart',
    cardsView: language === 'es' ? 'Tarjetas' : 'Cards',
    attacking: language === 'es' ? 'Atacando' : 'Attacking',
    defending: language === 'es' ? 'Defendiendo' : 'Defending',
    legend: language === 'es' ? 'Leyenda' : 'Legend',
    superEffective: language === 'es' ? 'Super efectivo (x2)' : 'Super effective (x2)',
    notVeryEffective: language === 'es' ? 'Poco efectivo (x0.5)' : 'Not very effective (x0.5)',
    noEffect: language === 'es' ? 'Sin efecto (x0)' : 'No effect (x0)',
    clickType: language === 'es' ? 'Haz clic en un tipo para ver detalles' : 'Click a type to see details'
  };
  
  return (
    <div className="min-h-screen" data-testid="types-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">
            <span className="text-violet-600 dark:text-violet-400">{texts.title}</span>
          </h1>
          <p className="text-muted-foreground">{texts.subtitle}</p>
        </motion.div>
        
        {/* View Toggle */}
        <div className="flex justify-center gap-2 mb-6">
          <button
            onClick={() => setViewMode('chart')}
            className={cn(
              'px-4 py-2 rounded-lg font-medium transition-colors',
              viewMode === 'chart' 
                ? 'bg-violet-600 text-white' 
                : 'bg-card hover:bg-muted text-foreground'
            )}
            data-testid="view-chart-btn"
          >
            {texts.chartView}
          </button>
          <button
            onClick={() => setViewMode('cards')}
            className={cn(
              'px-4 py-2 rounded-lg font-medium transition-colors',
              viewMode === 'cards' 
                ? 'bg-violet-600 text-white' 
                : 'bg-card hover:bg-muted text-foreground'
            )}
            data-testid="view-cards-btn"
          >
            {texts.cardsView}
          </button>
        </div>
        
        {viewMode === 'chart' ? (
          <>
            {/* Legend */}
            <div className="bg-card rounded-xl border border-border p-4 mb-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Info className="w-4 h-4" />
                {texts.legend}
              </h3>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 bg-green-900/50 text-green-300 rounded flex items-center justify-center font-bold text-xs">2</span>
                  <span>{texts.superEffective}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 bg-red-900/50 text-red-300 rounded flex items-center justify-center font-bold text-xs">½</span>
                  <span>{texts.notVeryEffective}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 bg-gray-800 text-gray-400 rounded flex items-center justify-center font-bold text-xs">0</span>
                  <span>{texts.noEffect}</span>
                </div>
              </div>
            </div>
            
            {/* Type Chart Table */}
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[700px]">
                  <thead>
                    <tr>
                      <th className="sticky left-0 z-20 bg-card p-1 text-xs w-[68px]">
                        <div className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                          <ArrowRight className="w-2.5 h-2.5" />
                          <span>{texts.defending}</span>
                        </div>
                      </th>
                      {ALL_TYPES.map(type => (
                        <th 
                          key={type} 
                          className="p-0.5 text-center cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => setSelectedType(type)}
                        >
                          <div 
                            className={cn(
                              'w-7 h-7 rounded flex items-center justify-center text-[10px] font-bold mx-auto',
                              TYPE_COLORS[type]?.bg,
                              TYPE_COLORS[type]?.text
                            )}
                            title={TYPE_NAMES[type]?.[language]}
                          >
                            {type.substring(0, 3).toUpperCase()}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ALL_TYPES.map(attacker => (
                      <tr key={attacker} className="hover:bg-muted/30 transition-colors">
                        <td 
                          className="sticky left-0 z-10 bg-card p-1 cursor-pointer hover:bg-muted/50"
                          onClick={() => setSelectedType(attacker)}
                        >
                          <div 
                            className={cn(
                              'w-16 h-6 rounded flex items-center justify-center text-[10px] font-bold',
                              TYPE_COLORS[attacker]?.bg,
                              TYPE_COLORS[attacker]?.text
                            )}
                          >
                            {TYPE_NAMES[attacker]?.[language]?.substring(0, 6).toUpperCase() || attacker.substring(0, 6).toUpperCase()}
                          </div>
                        </td>
                        {ALL_TYPES.map(defender => (
                          <EffectivenessCell 
                            key={`${attacker}-${defender}`}
                            value={effectivenessMatrix[attacker][defender]}
                          />
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Selected Type Detail */}
            {selectedType && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6"
              >
                <TypeDetailCard type={selectedType} />
              </motion.div>
            )}
            
            {!selectedType && (
              <p className="text-center text-muted-foreground text-sm mt-4">
                {texts.clickType}
              </p>
            )}
          </>
        ) : (
          /* Cards View */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ALL_TYPES.map(type => (
              <TypeDetailCard key={type} type={type} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
